/**
 * useWebSocket — hook React que gerencia a conexão WebSocket com o backend.
 *
 * Substitui usePolling para o Call Center com suporte a:
 * - Conexão autenticada (JWT como query param)
 * - Reconexão com backoff exponencial (1s, 2s, 4s, 8s, ..., 30s)
 * - Heartbeat a cada 5 minutos com timeout de 10s para pong
 * - Indicador de status (connected, reconnecting, disconnected)
 * - Enfileiramento de eventos quando aba está inativa (Page Visibility API)
 * - Fallback polling: pollingDisabled = true quando WS conectado >=10s
 * - Sync pós-reconexão via onReconnect callback
 *
 * @param {Object} options
 * @param {string} options.tenantId
 * @param {string} options.token - JWT token
 * @param {Function} options.onEvent - Callback para eventos recebidos
 * @param {string[]} [options.groups] - Grupos do atendente
 * @param {Function} [options.onReconnect] - Callback chamado após reconexão (não na conexão inicial). Retorna Promise<boolean>
 * @returns {{ status: string, send: Function, disconnect: Function, pollingDisabled: boolean }}
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const HEARTBEAT_INTERVAL = 300000; // 5 minutes
const PONG_TIMEOUT = 10000; // 10 seconds
const INITIAL_CONNECTION_TIMEOUT = 5000; // 5 seconds
const MAX_BACKOFF = 30000; // 30 seconds
const STABLE_CONNECTION_DELAY = 10000; // 10 seconds before disabling polling
const RECONNECT_SYNC_TIMEOUT = 10000; // 10 seconds timeout for onReconnect sync

/**
 * Calcula o delay de backoff exponencial para uma dada tentativa.
 * @param {number} attempt - Número da tentativa (0-indexed)
 * @returns {number} Delay em milissegundos
 */
export function calculateBackoff(attempt) {
  return Math.min(Math.pow(2, attempt) * 1000, MAX_BACKOFF);
}

export function useWebSocket({ tenantId, token, onEvent, groups, onReconnect }) {
  const [status, setStatus] = useState('disconnected');
  const [pollingDisabled, setPollingDisabled] = useState(false);
  const wsRef = useRef(null);
  const attemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const pongTimerRef = useRef(null);
  const initialTimeoutRef = useRef(null);
  const stableTimerRef = useRef(null);
  const isInitialRef = useRef(true);
  const hasConnectedOnceRef = useRef(false);
  const eventQueueRef = useRef([]);
  const isHiddenRef = useRef(document.hidden);
  const onEventRef = useRef(onEvent);
  const onReconnectRef = useRef(onReconnect);
  const disconnectedRef = useRef(false);
  const tokenRef = useRef(token);
  const groupsRef = useRef(groups);
  const tenantIdRef = useRef(tenantId);

  // Keep refs up-to-date without triggering re-renders
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);
  useEffect(() => { onReconnectRef.current = onReconnect; }, [onReconnect]);
  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { groupsRef.current = groups; }, [groups]);
  useEffect(() => { tenantIdRef.current = tenantId; }, [tenantId]);

  const clearTimers = useCallback(() => {
    clearTimeout(reconnectTimerRef.current);
    clearInterval(heartbeatTimerRef.current);
    clearTimeout(pongTimerRef.current);
    clearTimeout(initialTimeoutRef.current);
    clearTimeout(stableTimerRef.current);
    reconnectTimerRef.current = null;
    heartbeatTimerRef.current = null;
    pongTimerRef.current = null;
    initialTimeoutRef.current = null;
    stableTimerRef.current = null;
  }, []);

  const startStableTimer = useCallback(() => {
    clearTimeout(stableTimerRef.current);
    stableTimerRef.current = setTimeout(() => {
      console.log('[WS Debug] 10s stable — disabling polling');
      setPollingDisabled(true);
    }, STABLE_CONNECTION_DELAY);
  }, []);

  const startHeartbeat = useCallback(() => {
    clearInterval(heartbeatTimerRef.current);
    heartbeatTimerRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ action: 'ping' }));
        // Wait for pong response
        pongTimerRef.current = setTimeout(() => {
          // No pong received — connection is dead
          if (wsRef.current) {
            wsRef.current.close();
          }
        }, PONG_TIMEOUT);
      }
    }, HEARTBEAT_INTERVAL);
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (disconnectedRef.current) return;

    const delay = calculateBackoff(attemptRef.current);
    attemptRef.current += 1;
    setStatus('reconnecting');
    // Immediately reactivate polling on disconnect/reconnecting
    setPollingDisabled(false);
    clearTimeout(stableTimerRef.current);
    stableTimerRef.current = null;

    reconnectTimerRef.current = setTimeout(() => {
      connect(); // eslint-disable-line no-use-before-define
    }, delay);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReconnectSync = useCallback(async (ws) => {
    if (!onReconnectRef.current) {
      // No onReconnect callback — proceed normally
      startStableTimer();
      return;
    }

    try {
      const syncResult = await Promise.race([
        onReconnectRef.current(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('sync_timeout')), RECONNECT_SYNC_TIMEOUT)
        ),
      ]);

      if (syncResult === false) {
        throw new Error('sync_failed');
      }

      // Sync succeeded — start stable timer
      startStableTimer();
    } catch {
      // Sync failed or timed out: close WS, keep pollingDisabled = false, restart backoff
      setPollingDisabled(false);
      clearTimeout(stableTimerRef.current);
      stableTimerRef.current = null;

      if (ws && ws.readyState !== WebSocket.CLOSED && ws.readyState !== WebSocket.CLOSING) {
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.onopen = null;
        ws.close();
      }
      wsRef.current = null;
      clearInterval(heartbeatTimerRef.current);
      clearTimeout(pongTimerRef.current);

      // Restart backoff reconnection
      if (!disconnectedRef.current) {
        scheduleReconnect();
      }
    }
  }, [startStableTimer, scheduleReconnect]); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = useCallback(() => {
    if (disconnectedRef.current) return;
    const wsUrl = process.env.REACT_APP_WS_URL;
    if (!wsUrl || !tokenRef.current) {
      setStatus('disconnected');
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.onopen = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    const ws = new WebSocket(`${wsUrl}?token=${tokenRef.current}`);
    wsRef.current = ws;

    // Initial connection timeout — only for first connection
    if (isInitialRef.current) {
      initialTimeoutRef.current = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close();
          setStatus('disconnected');
          isInitialRef.current = false;
          // Continue reconnection in background but keep status as disconnected
          // until connection actually succeeds
          scheduleReconnect();
        }
      }, INITIAL_CONNECTION_TIMEOUT);
    }

    ws.onopen = () => {
      clearTimeout(initialTimeoutRef.current);
      const isReconnection = hasConnectedOnceRef.current;
      isInitialRef.current = false;
      hasConnectedOnceRef.current = true;
      attemptRef.current = 0;
      setStatus('connected');
      startHeartbeat();
      console.log(`[WS Debug] onopen — isReconnection=${isReconnection}`);

      if (isReconnection) {
        // Call onReconnect for sync after reconnection
        handleReconnectSync(ws);
      } else {
        // Initial connection — just start the stable timer
        startStableTimer();
      }
    };

    ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        // eslint-disable-next-line no-console
        console.warn('[useWebSocket] Malformed message received:', event.data);
        return;
      }

      // Handle pong response — clear pong timeout
      if (data.action === 'pong') {
        clearTimeout(pongTimerRef.current);
        pongTimerRef.current = null;
        return;
      }

      // If tab is hidden, queue events
      if (isHiddenRef.current) {
        eventQueueRef.current.push(data);
        return;
      }

      // Deliver event immediately
      if (onEventRef.current) {
        console.log('[WS Debug] event received:', data.event || data.action || 'unknown', data);
        onEventRef.current(data);
      }
    };

    ws.onclose = () => {
      clearInterval(heartbeatTimerRef.current);
      clearTimeout(pongTimerRef.current);
      clearTimeout(stableTimerRef.current);
      stableTimerRef.current = null;
      wsRef.current = null;
      console.log('[WS Debug] onclose — reactivating polling, scheduling reconnect');

      // Immediately reactivate polling
      setPollingDisabled(false);

      if (!disconnectedRef.current) {
        scheduleReconnect();
      }
    };

    ws.onerror = () => {
      // onerror is always followed by onclose — let onclose handle reconnection
    };
  }, [scheduleReconnect, startHeartbeat, startStableTimer, handleReconnectSync]); // eslint-disable-line react-hooks/exhaustive-deps

  // Page Visibility API — queue events when tab is hidden, flush on visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      isHiddenRef.current = document.hidden;

      if (!document.hidden && eventQueueRef.current.length > 0) {
        // Flush queued events in order
        const queue = eventQueueRef.current.slice();
        eventQueueRef.current = [];
        queue.forEach((data) => {
          if (onEventRef.current) {
            onEventRef.current(data);
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Main connection effect
  useEffect(() => {
    disconnectedRef.current = false;
    isInitialRef.current = true;
    hasConnectedOnceRef.current = false;
    attemptRef.current = 0;
    setPollingDisabled(false);
    connect();

    return () => {
      disconnectedRef.current = true;
      clearTimers();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;
        wsRef.current.onopen = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      setStatus('disconnected');
      setPollingDisabled(false);
    };
  }, [tenantId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  const disconnect = useCallback(() => {
    disconnectedRef.current = true;
    clearTimers();
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.onopen = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setStatus('disconnected');
    setPollingDisabled(false);
  }, [clearTimers]);

  return { status, send, disconnect, pollingDisabled };
}
